import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Grid,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import moment from "moment";
import TicketPrint from "./TicketPrint";

export default function TicketList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [printTicketOpen, setPrintTicketOpen] = useState(false);
  const [ticketToPrint, setTicketToPrint] = useState(null);

  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["tickets", page, rowsPerPage, searchTerm, statusFilter],
    `tickets/?page=${page}&page_size=${rowsPerPage}&search=${searchTerm}&status=${statusFilter}`,
    token
  );

  const tickets = data?.results || [];
  const totalCount = data?.count || 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 1: // Active
        return "success";
      case 2: // Completed
        return "info";
      case 3: // Cancelled
        return "error";
      default:
        return "default";
    }
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/reception/ticketing/tickets/${ticketId}`);
  };

  const handlePrintTicket = (ticket) => {
    setTicketToPrint(ticket);
    setPrintTicketOpen(true);
  };

  const handleCreateTicket = () => {
    navigate("/reception/ticketing/tickets/new");
  };

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || "Failed to load tickets"}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Patient Tickets
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <TextField
              size="small"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: 150 }}
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="2">Completed</option>
              <option value="3">Cancelled</option>
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTicket}
            >
              New Ticket
            </Button>
          </Grid>
        </Grid>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Ticket Number</strong></TableCell>
                  <TableCell><strong>Patient</strong></TableCell>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell><strong>Visit Date</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Encounters</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No tickets found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tickets.map((ticket) => (
                    <TableRow key={ticket.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {ticket.ticket_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{ticket.patient_name_display || "N/A"}</TableCell>
                      <TableCell>{ticket.department_name_display || "N/A"}</TableCell>
                      <TableCell>
                        {moment(ticket.visit_date).format("YYYY-MM-DD HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.status_display}
                          color={getStatusColor(ticket.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{ticket.encounters_count || 0}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewTicket(ticket.id)}
                            color="primary"
                            title="View Ticket"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handlePrintTicket(ticket)}
                            color="secondary"
                            title="Print Ticket"
                          >
                            <PrintIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page - 1}
              onPageChange={(e, newPage) => setPage(newPage + 1)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        )}
      </Paper>

      {/* Print Ticket Dialog */}
      <Dialog
        open={printTicketOpen}
        onClose={() => setPrintTicketOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          {ticketToPrint && (
            <TicketPrint
              ticket={ticketToPrint}
              onClose={() => setPrintTicketOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
